import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import type { AssignPreview } from "@/lib/types"

// Dates arrive as "YYYY-MM-DD" (or an ISO timestamp). Date-only strings are
// built as local dates so the day does not shift with the viewer's timezone.
function formatDate(value: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value)
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

interface AssignSummaryProps {
  ready: boolean
  loading: boolean
  current: boolean
  error: string | null
  preview: AssignPreview | null
  assigning: boolean
  onAssign: () => void
  // Group chips were clicked, but hand-picked people made this people mode.
  oneOffFromGroups: boolean
}

export function AssignSummary({
  ready,
  loading,
  current,
  error,
  preview,
  assigning,
  onAssign,
  oneOffFromGroups,
}: AssignSummaryProps) {
  const canAssign = ready && current && !loading && !error && preview !== null && !assigning

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Summary</CardTitle>
      </CardHeader>
      <CardContent>
        {!ready ? (
          <p className="text-sm text-muted-foreground">
            Pick at least one assessment and one person or group to see what
            will happen.
          </p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : !preview ? (
          <p className="text-sm text-muted-foreground">Checking...</p>
        ) : (
          <ul className={`space-y-1.5 text-sm ${loading ? "opacity-60" : ""}`}>
            {preview.rounds.map((round) => (
              <li key={`round-${round.quiz_id}-${round.group_id ?? "people"}`}>
                Open a round for {round.user_ids.length}{" "}
                {round.user_ids.length === 1 ? "person" : "people"} in{" "}
                {round.group_name ?? "selected people"} for {round.quiz_title},{" "}
                {round.due_on ? `due ${formatDate(round.due_on)}` : "no due date"}
              </li>
            ))}
            {preview.replacing.map((entry) => (
              <li key={`replace-${entry.assignment_id}`}>
                {entry.status === "passed"
                  ? `Close ${entry.staff_name}'s passed round from ${formatDate(entry.assigned_on)}. It stays reviewable.`
                  : `Close ${entry.staff_name}'s unfinished round from ${formatDate(entry.assigned_on)}`}
              </li>
            ))}
            {preview.moved_to_earlier_group.map((entry) => (
              <li key={`moved-${entry.user_id}`}>
                {entry.staff_name} is in more than one group and goes into{" "}
                {entry.group_name}
              </li>
            ))}
            {preview.overlapping_schedules.map((entry) => (
              <li key={`overlap-${entry.schedule_id}`}>
                {entry.group_name} already repeats {entry.quiz_title}. Shared
                members will get both.
              </li>
            ))}
            {oneOffFromGroups && (
              <li>
                Your changes to the group selection mean these will be one-off
                assignments.
              </li>
            )}
            {preview.next_opens_on && (
              <li>Next round opens {formatDate(preview.next_opens_on)}</li>
            )}
          </ul>
        )}
      </CardContent>
      <CardFooter>
        <Button className="w-full sm:w-auto" onClick={onAssign} disabled={!canAssign}>
          {assigning ? "Assigning..." : "Assign"}
        </Button>
      </CardFooter>
    </Card>
  )
}
